/**
 * Single source of truth for `.env`-file classification, shared by:
 *   - the shell `PreToolUse` guard (`hooks/trace-mcp-guard.sh`), via the
 *     `trace-mcp classify-env <path>` CLI subcommand
 *   - the `get_env_vars` MCP tool (when it considers a redacted view)
 *   - any future call site that needs to ask "is reading this file safe?"
 *
 * Prior to this module, three independent models lived in three files
 * (shell regex, TS `isSensitiveFile`, and ad-hoc string checks). They
 * disagreed on tool-managed files such as `~/.trace-mcp/launcher.env`
 * and caused false positives in the guard's coach output.
 *
 * The classifier returns one of three tiers:
 *   - "template"     — committed example/sample/template file
 *   - "managed"      — generated by a trusted tool (provenance marker)
 *   - "user-secret"  — anything else that matches the .env naming rule
 *
 * Provenance is a *trust signal*, not a content guarantee: the allowlist
 * is closed and matched by exact string. A `.env` whose first line is
 * `# Managed by attacker` does not become "managed".
 */

import path from 'node:path';

export type EnvFileTier = 'template' | 'managed' | 'user-secret' | 'not-env';

export interface EnvClassification {
  tier: EnvFileTier;
  /** Human-readable reasons; useful for hook diagnostics and tests. */
  reasons: string[];
}

/**
 * Tools whose generated `.env`-shaped files we trust enough to skip the
 * "read .env via get_env_vars" advisory. Matched by exact string against
 * the header comment, after the literal `# Managed by ` prefix.
 *
 * Keep this list closed and explicit. Adding an entry is a security
 * decision: the emitter must guarantee that values are non-sensitive
 * (e.g. only paths, versions, feature flags — never tokens or keys).
 */
export const MANAGED_EMITTER_ALLOWLIST: readonly string[] = ['trace-mcp'];

const ENV_FILE_RE = /\.env(\.[a-zA-Z0-9._-]+)?$/i;
const ENV_TEMPLATE_RE = /\.env\.(examples?|samples?|templates?|dist|defaults?|docs?)$/i;
const MANAGED_HEADER_RE = /^#\s*Managed by\s+([A-Za-z0-9_.-]+)\b/;

/** How many bytes from the start of the file are scanned for a provenance marker. */
const HEAD_SCAN_BYTES = 512;

/**
 * Classify a `.env`-shaped file path (and optionally a peek at its head).
 *
 * `headBytes` is optional: callers that already have the bytes in hand
 * (e.g. the CLI subcommand reading from disk) pass them through; callers
 * that only have a path can leave it undefined and only the filename rule
 * is applied — which yields `template` or `user-secret`, never `managed`.
 */
export function classifyEnvFile(absPath: string, headBytes?: string): EnvClassification {
  const base = path.basename(absPath);
  const reasons: string[] = [];

  if (!ENV_FILE_RE.test(base)) {
    return { tier: 'not-env', reasons: ['filename does not match .env pattern'] };
  }

  if (ENV_TEMPLATE_RE.test(base)) {
    reasons.push(`filename matches template allowlist (${base})`);
    return { tier: 'template', reasons };
  }

  if (headBytes !== undefined) {
    const emitter = detectManagedEmitter(headBytes);
    if (emitter) {
      reasons.push(`header declares trusted emitter "${emitter}"`);
      return { tier: 'managed', reasons };
    }
  }

  reasons.push('no template name, no trusted-emitter header — treat as user secret');
  return { tier: 'user-secret', reasons };
}

/**
 * Returns the emitter name if `headBytes` starts with a recognized
 * `# Managed by <name>` header and `<name>` is in the allowlist.
 * Otherwise undefined. Only the first non-blank line is inspected;
 * a malicious file cannot smuggle a marker by burying it lower.
 */
export function detectManagedEmitter(headBytes: string): string | undefined {
  const sample =
    headBytes.length > HEAD_SCAN_BYTES ? headBytes.slice(0, HEAD_SCAN_BYTES) : headBytes;
  for (const rawLine of sample.split('\n')) {
    const line = rawLine.trim();
    if (line === '') continue;
    const match = line.match(MANAGED_HEADER_RE);
    if (!match) return undefined;
    const emitter = match[1];
    return MANAGED_EMITTER_ALLOWLIST.includes(emitter) ? emitter : undefined;
  }
  return undefined;
}
